import { COVER_CROP_MIX_TYPE, IS_PRACTICE_APPLIED_BY_DEFAULT, OnboardingPracticeKey, SOIL_SIGHT_TILLAGE } from "consts";

import { CropYear } from "pages/app/scenario-analysis/types/scenario";
import { growsCoverCrop } from "pages/app/scenario-analysis/utils/cropYearHelpers";
import { RecommendationField, RecommendationMap } from "pages/app/scenario-onboarding/ScenarioOnboardingContext";
import { GenerateChangeScenarioResult } from "pages/app/scenario-onboarding/utils/changeScenarioGenerator";

/** The crop-year field each step-3 practice toggle controls. */
export const FIELD_BY_PRACTICE: Record<OnboardingPracticeKey, RecommendationField> = {
  residue: "residue",
  tillage: "tillageId",
  coverCrop: "coverCropId",
};

const PRACTICE_FIELDS = Object.values(FIELD_BY_PRACTICE);

/** Treats the interchangeable "nothing selected" encodings (`false`/`"none"` vs `null`) as equal. */
export const isSameRecommendationValue = (field: RecommendationField, a: CropYear | undefined, b: CropYear | undefined): boolean => {
  if (!a || !b) return false;
  if (field === "residue") return Boolean(a.residue) === Boolean(b.residue);
  if (field === "coverCropId") return (a.coverCropId || COVER_CROP_MIX_TYPE.NONE) === (b.coverCropId || COVER_CROP_MIX_TYPE.NONE);
  return (a.tillageId ?? null) === (b.tillageId ?? null);
};

/**
 * Rewrites one practice's field on every row from its counterpart in `sourceRows` (generated = on,
 * baseline = off). Returns `rows` itself when nothing moves, so a switch with nothing to apply cannot
 * trigger a save and recalculation that would return the same numbers.
 */
export const applyPracticeToRows = (rows: CropYear[], sourceRows: CropYear[], field: RecommendationField): CropYear[] => {
  const sourceById = new Map(sourceRows.map((year) => [year.id, year]));
  let hasChange = false;

  const next = rows.map((row) => {
    const source = sourceById.get(row.id);
    if (!source || isSameRecommendationValue(field, row, source)) return row;
    hasChange = true;
    return { ...row, [field]: source[field] };
  });

  return hasChange ? next : rows;
};

/** True when neither switching a practice on nor off would change a single row — the switch cannot move. */
export const isPracticeToggleInert = (rows: CropYear[], generatedYears: CropYear[], baselineYears: CropYear[], field: RecommendationField): boolean =>
  applyPracticeToRows(rows, generatedYears, field) === rows && applyPracticeToRows(rows, baselineYears, field) === rows;

/** True when any row already follows the practice: residue left on the field, no-till, or a cover crop sown. */
export const isPracticeInRows = (rows: CropYear[], field: RecommendationField): boolean =>
  rows.some((row) => {
    if (field === "residue") return Boolean(row.residue);
    if (field === "coverCropId") return growsCoverCrop(row);
    return row.tillageId === SOIL_SIGHT_TILLAGE.NONE;
  });

/** "On" while every change row still holds the generated value for that practice; empty rows read as its default. */
export const getAppliedPractices = (generated: GenerateChangeScenarioResult, changeYears: CropYear[]): Record<OnboardingPracticeKey, boolean> => {
  const generatedById = new Map(generated.changeYears.map((year) => [year.id, year]));

  return (Object.keys(FIELD_BY_PRACTICE) as OnboardingPracticeKey[]).reduce(
    (result, practice) => ({
      ...result,
      [practice]:
        changeYears.length === 0
          ? IS_PRACTICE_APPLIED_BY_DEFAULT[practice]
          : // A row the generator never produced cannot disagree with it.
            changeYears.every((row) => !generatedById.has(row.id) || isSameRecommendationValue(FIELD_BY_PRACTICE[practice], row, generatedById.get(row.id))),
    }),
    {} as Record<OnboardingPracticeKey, boolean>
  );
};

/** Cells to highlight: fields the generator moved off the baseline, on rows that still hold the generated value. */
export const getHighlightedFieldsByYearId = (generatedYears: CropYear[], baselineYears: CropYear[], changeYears: CropYear[]): RecommendationMap => {
  const baselineById = new Map(baselineYears.map((year) => [year.id, year]));
  const changeById = new Map(changeYears.map((year) => [year.id, year]));
  const highlighted: RecommendationMap = {};

  generatedYears.forEach((generatedYear) => {
    const baselineYear = baselineById.get(generatedYear.id);
    const changeYear = changeById.get(generatedYear.id);
    if (!baselineYear || !changeYear) return;

    const fields = PRACTICE_FIELDS.filter(
      (field) => !isSameRecommendationValue(field, generatedYear, baselineYear) && isSameRecommendationValue(field, changeYear, generatedYear)
    );
    if (fields.length > 0) highlighted[generatedYear.id] = fields;
  });

  return highlighted;
};

export const countChangedPractices = (baselineYears: CropYear[], changeYears: CropYear[]): number => {
  const baselineById = new Map(baselineYears.map((year) => [year.id, year]));
  return PRACTICE_FIELDS.filter((field) => changeYears.some((year) => !isSameRecommendationValue(field, baselineById.get(year.id), year))).length;
};
